import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  LogOut, Mail, Calendar, Filter, Search, X, 
  MessageSquare, User, ChevronLeft, ChevronRight, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ onLogout }) => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, responded: 0 });
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const contactsPerPage = 10;

  const getToken = () => localStorage.getItem('admin_token');

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      
      const response = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params
      });
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchStats();
    fetchContacts();
  }, [filterStatus, fetchStats, fetchContacts]);

  const updateContactStatus = async (contactId, status, notes) => {
    try {
      await axios.put(
        `${API}/admin/contacts/${contactId}`,
        { status, notes },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Contact updated successfully');
      fetchContacts();
      fetchStats();
      setSelectedContact(null);
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Contact deleted successfully');
      fetchContacts();
      fetchStats();
      setSelectedContact(null);
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleLogout = () => {
    logout();
    onLogout();
  };

  // Filter and search
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Pagination
  const indexOfLastContact = currentPage * contactsPerPage;
  const indexOfFirstContact = indexOfLastContact - contactsPerPage;
  const currentContacts = filteredContacts.slice(indexOfFirstContact, indexOfLastContact);
  const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'contacted': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'responded': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <div className="glass border-b border-white/5">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl text-gold">Admin Dashboard</h1>
              <p className="text-sm text-white/60 mt-1">Welcome, {user?.username}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 border border-white/10 hover:border-gold/50 text-white/80 hover:text-gold transition-colors rounded"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass p-6 border border-white/10 rounded-lg"
          >
            <div className="text-3xl font-bold text-gold mb-2">{stats.total}</div>
            <div className="text-sm text-white/60">Total Contacts</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass p-6 border border-white/10 rounded-lg"
          >
            <div className="text-3xl font-bold text-blue-400 mb-2">{stats.new}</div>
            <div className="text-sm text-white/60">New</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass p-6 border border-white/10 rounded-lg"
          >
            <div className="text-3xl font-bold text-yellow-400 mb-2">{stats.contacted}</div>
            <div className="text-sm text-white/60">Contacted</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass p-6 border border-white/10 rounded-lg"
          >
            <div className="text-3xl font-bold text-green-400 mb-2">{stats.responded}</div>
            <div className="text-sm text-white/60">Responded</div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="glass border border-white/10 rounded-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded px-10 py-2 text-white focus:outline-none focus:border-gold/50 transition-colors"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterStatus('')}
                className={`px-4 py-2 rounded transition-colors ${
                  filterStatus === '' 
                    ? 'bg-gold text-black' 
                    : 'border border-white/10 text-white/60 hover:border-gold/50 hover:text-gold'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterStatus('new')}
                className={`px-4 py-2 rounded transition-colors ${
                  filterStatus === 'new' 
                    ? 'bg-blue-500 text-white' 
                    : 'border border-white/10 text-white/60 hover:border-blue-500/50 hover:text-blue-400'
                }`}
              >
                New
              </button>
              <button
                onClick={() => setFilterStatus('contacted')}
                className={`px-4 py-2 rounded transition-colors ${
                  filterStatus === 'contacted' 
                    ? 'bg-yellow-500 text-black' 
                    : 'border border-white/10 text-white/60 hover:border-yellow-500/50 hover:text-yellow-400'
                }`}
              >
                Contacted
              </button>
              <button
                onClick={() => setFilterStatus('responded')}
                className={`px-4 py-2 rounded transition-colors ${
                  filterStatus === 'responded' 
                    ? 'bg-green-500 text-white' 
                    : 'border border-white/10 text-white/60 hover:border-green-500/50 hover:text-green-400'
                }`}
              >
                Responded
              </button>
            </div>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="glass border border-white/10 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-white/60">Loading...</div>
          ) : currentContacts.length === 0 ? (
            <div className="p-12 text-center text-white/60">No contacts found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-white/10">
                    <tr className="text-left">
                      <th className="px-6 py-4 text-sm font-semibold text-white/80">Name</th>
                      <th className="px-6 py-4 text-sm font-semibold text-white/80">Email</th>
                      <th className="px-6 py-4 text-sm font-semibold text-white/80">Project Type</th>
                      <th className="px-6 py-4 text-sm font-semibold text-white/80">Status</th>
                      <th className="px-6 py-4 text-sm font-semibold text-white/80">Date</th>
                      <th className="px-6 py-4 text-sm font-semibold text-white/80">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentContacts.map((contact, index) => (
                      <motion.tr
                        key={contact.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => setSelectedContact(contact)}
                      >
                        <td className="px-6 py-4 text-white/80">{contact.name}</td>
                        <td className="px-6 py-4 text-white/60 text-sm">{contact.email}</td>
                        <td className="px-6 py-4 text-white/60 text-sm">{contact.project_type || '-'}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(contact.status)}`}>
                            {contact.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-white/60 text-sm">{formatDate(contact.timestamp)}</td>
                        <td className="px-6 py-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteContact(contact.id);
                            }}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                  <div className="text-sm text-white/60">
                    Showing {indexOfFirstContact + 1} to {Math.min(indexOfLastContact, filteredContacts.length)} of {filteredContacts.length} contacts
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-white/10 rounded hover:border-gold/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <span className="px-4 py-1 text-white/80">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-white/10 rounded hover:border-gold/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Contact Detail Modal */}
      <AnimatePresence>
        {selectedContact && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-6 z-50"
            onClick={() => setSelectedContact(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass border border-white/10 rounded-lg p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <h2 className="font-serif text-2xl text-gold">Contact Details</h2>
                <button
                  onClick={() => setSelectedContact(null)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Name</label>
                  <div className="flex items-center gap-2 text-white">
                    <User size={18} className="text-gold" />
                    <span>{selectedContact.name}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-white/60 mb-1 block">Email</label>
                  <div className="flex items-center gap-2 text-white">
                    <Mail size={18} className="text-gold" />
                    <a href={`mailto:${selectedContact.email}`} className="hover:text-gold transition-colors">
                      {selectedContact.email}
                    </a>
                  </div>
                </div>

                {selectedContact.project_type && (
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">Project Type</label>
                    <div className="text-white">{selectedContact.project_type}</div>
                  </div>
                )}

                {selectedContact.subject && (
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">Subject</label>
                    <div className="text-white">{selectedContact.subject}</div>
                  </div>
                )}

                <div>
                  <label className="text-sm text-white/60 mb-1 block">Message</label>
                  <div className="bg-white/5 border border-white/10 rounded p-4 text-white whitespace-pre-wrap">
                    {selectedContact.message}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-white/60 mb-1 block">Date</label>
                  <div className="flex items-center gap-2 text-white">
                    <Calendar size={18} className="text-gold" />
                    <span>{formatDate(selectedContact.timestamp)}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-white/60 mb-2 block">Status</label>
                  <div className="flex gap-2">
                    {['new', 'contacted', 'responded'].map((status) => (
                      <button
                        key={status}
                        onClick={() => updateContactStatus(selectedContact.id, status, selectedContact.notes)}
                        className={`px-4 py-2 rounded capitalize transition-colors ${
                          selectedContact.status === status
                            ? 'bg-gold text-black'
                            : 'border border-white/10 text-white/60 hover:border-gold/50 hover:text-gold'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedContact.notes && (
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">Notes</label>
                    <div className="bg-white/5 border border-white/10 rounded p-4 text-white">
                      {selectedContact.notes}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
